/**
 * Conventions for reading ActivityLog.meta. The shared ActivityLog type keeps
 * `meta` open (Record<string, string | number>), so the admin defines the keys
 * it expects here in ONE place. If the customer app (Agent C) emits different
 * keys, only this file changes.
 *
 * NOTE (shared-contract wish): consider narrowing ActivityLog.meta per-event in
 * @mitang/shared so producers/consumers can't drift. Until then these are the
 * agreed keys.
 */
import type { ActivityEvent, ActivityLog } from '@mitang/shared';

/** Human label for an event type. */
export const EVENT_LABEL: Record<ActivityEvent, string> = {
  app_open: 'App opened',
  session_end: 'Session ended',
  viewed_category: 'Viewed category',
  viewed_product: 'Viewed product',
  searched: 'Searched',
  shared_product: 'Shared product',
  filter_used: 'Used filter',
  wishlisted: 'Saved to wishlist',
  unwishlisted: 'Removed from wishlist',
  scratch_revealed: 'Scratched a card',
};

function metaStr(meta: ActivityLog['meta'], key: string): string | undefined {
  const v = meta?.[key];
  return v === undefined ? undefined : String(v);
}

function metaNum(meta: ActivityLog['meta'], key: string): number | undefined {
  const v = meta?.[key];
  return typeof v === 'number' ? v : undefined;
}

/** Internal product id for viewed_product / shared_product events. */
export function productIdOf(log: ActivityLog): string | undefined {
  return metaStr(log.meta, 'productId');
}

/** Model number for viewed_product / shared_product events. */
export function modelNumberOf(log: ActivityLog): string | undefined {
  return metaStr(log.meta, 'modelNumber');
}

/**
 * What a HUMAN should see for a product. The internal id is a slug of category
 * plus part number (`AIO_BD0A0PA`), which nobody recognises; the model number
 * (`HP AIO 24-CU0561IN`) is what staff and dealers actually use. The id is only
 * a fallback for events logged before the app started stamping the model.
 */
export function productLabelOf(log: ActivityLog): string | undefined {
  return modelNumberOf(log) ?? productIdOf(log);
}

/**
 * Category (i.e. the source sheet) for any event that has one.
 *
 * `viewed_category` events carry it directly. Product events carry it too now,
 * but older ones don't — so fall back to the category slug that is baked into
 * the front of every product id (`AIO_BD0A0PA` → `AIO`). Dashes come back as
 * spaces because the slug flattens them (`GAMING DSK` → `GAMING-DSK`).
 */
export function categoryOf(log: ActivityLog): string | undefined {
  const explicit = metaStr(log.meta, 'category');
  if (explicit) return explicit;
  const pid = productIdOf(log);
  const slug = pid?.includes('_') ? pid.slice(0, pid.indexOf('_')) : undefined;
  return slug ? slug.replace(/-/g, ' ') : undefined;
}

/** Search query text for searched events. */
export function queryOf(log: ActivityLog): string | undefined {
  return metaStr(log.meta, 'query');
}

/**
 * Explicit session duration (ms) if the customer app stamped one on a
 * session_end event. When absent, the timeline derives duration by pairing
 * app_open → session_end.
 */
export function durationMsOf(log: ActivityLog): number | undefined {
  return metaNum(log.meta, 'durationMs');
}

/** A concise, human-readable summary of a single log line for feeds/timelines. */
export function describeLog(log: ActivityLog): string {
  switch (log.event) {
    case 'viewed_product':
      return `Viewed product ${productLabelOf(log) ?? '(unknown)'}`;
    case 'shared_product':
      return `Shared product ${productLabelOf(log) ?? '(unknown)'}`;
    case 'wishlisted':
      return `Saved ${productLabelOf(log) ?? '(unknown)'} to wishlist`;
    case 'unwishlisted':
      return `Removed ${productLabelOf(log) ?? '(unknown)'} from wishlist`;
    case 'viewed_category':
      return `Viewed category ${categoryOf(log) ?? '(unknown)'}`;
    case 'searched':
      return `Searched "${queryOf(log) ?? ''}"`;
    case 'filter_used':
      return 'Used a filter';
    case 'scratch_revealed': {
      const prize = metaStr(log.meta, 'prizeTitle') ?? 'a card';
      return metaNum(log.meta, 'won') === 0 ? `Scratched a card — ${prize}` : `Scratched a card and won ${prize}`;
    }
    case 'app_open':
      return 'Opened the app';
    case 'session_end':
      return 'Ended a session';
    default:
      return EVENT_LABEL[log.event] ?? log.event;
  }
}
